import { Component, inject, Input, Output, EventEmitter, OnInit, OnDestroy, computed, signal, ViewChild, ElementRef } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { firstValueFrom } from 'rxjs';

import { UserFormFacade } from '../../../../../Facades/user-form.facade';
import { ListRolesFacade } from '../../../../../Facades/list-roles.facade';
import { ApiService } from '../../../../../Modules/Shared/Services/api.service';
import { SearchService } from '../../../../../Services/Intune/search.service';
import { ScopeItem } from '../../../../../Interfaces/api/users.interface';
import { COMMON_IMPORTS } from '../../../../Shared/common-imports';
import { AvLoadingItemComponent } from '../../../../Shared/Components/av-loading-item/av-loading-item.component';
import { ScopeEntity } from '../../../Interfaces/scope-entity.interface';

@Component({
  selector: 'app-user-create-step-2',
  imports: [COMMON_IMPORTS, AvLoadingItemComponent],
  templateUrl: './user-create-step-2.component.html',
  styleUrl: './user-create-step-2.component.scss',
})
export class UserCreateStep2Component implements OnInit, OnDestroy {
  @Input({ required: true }) formGroup!: FormGroup;
  @Output() submitRequested = new EventEmitter<void>();
  @ViewChild('roleSearchInput') roleSearchInput?: ElementRef<HTMLInputElement>;

  readonly facade = inject(UserFormFacade);
  private readonly _listRolesFacade = inject(ListRolesFacade);
  private readonly _apiService = inject(ApiService);
  private readonly _searchService = inject(SearchService);

  readonly filteredRoles = computed(() =>
    this._listRolesFacade.roles().filter(r => r.name?.toLowerCase() !== 'root')
  );

  readonly isRolesLoading = computed(() => this._listRolesFacade.isLoading());

  readonly permissionSearchTerms = signal<Record<number, string>>({});
  readonly permissionSearchResults = signal<Record<number, ScopeEntity[]>>({});
  readonly permissionSearchLoading = signal<Record<number, boolean>>({});
  readonly permissionSelectedScopes = signal<Record<number, ScopeEntity[]>>({});

  private readonly _searchTimers = new Map<number, ReturnType<typeof setTimeout>>();

  ngOnInit(): void {
    this.initRoleIdChangeSubscription();
  }

  ngOnDestroy(): void {
    this._searchTimers.forEach(t => clearTimeout(t));
    this._searchTimers.clear();
  }

  private initRoleIdChangeSubscription(): void {
    this.formGroup.controls['roleId'].valueChanges.subscribe((id: string) => {
      if (id) {
        this.facade.setSelectedRoleId(id);
        this.permissionSelectedScopes.set({});
        this.permissionSearchTerms.set({});
        this.permissionSearchResults.set({});
      }
    });
  }

  reset(): void {
    this.permissionSelectedScopes.set({});
    this.permissionSearchTerms.set({});
    this.permissionSearchResults.set({});
  }

  getScopes(): Record<string, ScopeItem[]> {
    const scopes: Record<string, ScopeItem[]> = {};
    Object.entries(this.permissionSelectedScopes()).forEach(([permId, entities]) => {
      if (entities.length > 0) {
        scopes[permId] = entities.map(e => ({ uuid: e.uuid, type: e.type, displayName: e.displayName }));
      }
    });
    return scopes;
  }

  onRoleSearchChange(event: Event): void {
    this._listRolesFacade.setSearchTerm((event.target as HTMLInputElement).value);
  }

  onRoleDropdownChange(opened: boolean): void {
    if (opened) {
      setTimeout(() => this.roleSearchInput?.nativeElement.focus(), 50);
    } else {
      this._listRolesFacade.setSearchTerm('');
    }
  }

  getPermissionSearchTerm(permissionId: number): string {
    return this.permissionSearchTerms()[permissionId] ?? '';
  }

  getPermissionSearchResults(permissionId: number): ScopeEntity[] {
    return this.permissionSearchResults()[permissionId] ?? [];
  }

  getSelectedEntities(permissionId: number): ScopeEntity[] {
    return this.permissionSelectedScopes()[permissionId] ?? [];
  }

  getEntityIcon(type: string): string {
    const icons: Record<string, string> = { user: 'person', group: 'group', device: 'computer' };
    return icons[type] ?? 'help_outline';
  }

  onPermissionSearch(permissionId: number, event: Event): void {
    const term = (event.target as HTMLInputElement).value;
    this.permissionSearchTerms.update(m => ({ ...m, [permissionId]: term }));
    this._cancelPendingSearch(permissionId);

    if (term.length > 2) {
      this._scheduleSearch(permissionId, term);
    } else {
      this.permissionSearchResults.update(m => ({ ...m, [permissionId]: [] }));
    }
  }

  private _cancelPendingSearch(permissionId: number): void {
    const existing = this._searchTimers.get(permissionId);
    if (existing) clearTimeout(existing);
  }

  private _scheduleSearch(permissionId: number, term: string): void {
    this.permissionSearchLoading.update(m => ({ ...m, [permissionId]: true }));
    this._searchTimers.set(permissionId, setTimeout(() => this._executeSearch(permissionId, term), 300));
  }

  private async _executeSearch(permissionId: number, term: string): Promise<void> {
    try {
      const response: any = await firstValueFrom(
        this._apiService.run<any>(
          this._searchService.index(term, { users: '1', groups: '1', devices: '1' })
        )
      );
      const data = response?.data ?? {};
      this.permissionSearchResults.update(m => ({ ...m, [permissionId]: this._mapToScopeEntities(data) }));
    } finally {
      this.permissionSearchLoading.update(m => ({ ...m, [permissionId]: false }));
    }
  }

  onEntitySelected(permissionId: number, event: MatAutocompleteSelectedEvent): void {
    const entity = event.option.value as ScopeEntity;
    const current = this.permissionSelectedScopes()[permissionId] ?? [];
    if (!current.find(e => e.uuid === entity.uuid)) {
      this.permissionSelectedScopes.update(m => ({ ...m, [permissionId]: [...current, entity] }));
    }
    this.permissionSearchTerms.update(m => ({ ...m, [permissionId]: '' }));
    this.permissionSearchResults.update(m => ({ ...m, [permissionId]: [] }));
  }

  removeEntity(permissionId: number, entity: ScopeEntity): void {
    this.permissionSelectedScopes.update(m => ({
      ...m,
      [permissionId]: (m[permissionId] ?? []).filter(e => e.uuid !== entity.uuid),
    }));
  }

  private _mapToScopeEntities(data: { users?: any[]; groups?: any[] }): ScopeEntity[] {
    const entities: ScopeEntity[] = [];
    (data.users ?? []).forEach(u => entities.push({ uuid: u.id, type: 'user', displayName: u.displayName ?? u.userPrincipalName ?? u.id }));
    (data.groups ?? []).forEach(g => entities.push({ uuid: g.id, type: 'group', displayName: g.displayName ?? g.id }));
    return entities;
  }
}
